import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
    AppBar,
    Alert,
    Badge,
    Box,
    Button,
    Chip,
    Dialog,
    Drawer,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Slide,
    Stack,
    Toolbar,
    Typography,
    Tooltip,
    TextField,
    useMediaQuery,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { LoadingButton } from '@mui/lab';
import _ from 'lodash';
import { forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DialogsContext } from '../../../context/dialogsContext';
import { ReviewCasesContext } from '../../../context/review/ReviewCasesContext';
import { ReviewSessionsContext } from '../../../context/review/ReviewSessionsContext';
import { UserContext } from '../../../context/userContext';
import { AuthContext } from '../../../context/authContext';
import theme from '../../../Styles/CoreTheme';
import { CaseStatusType, CaseType, SessionType } from '../../../types';
import ExpandableText from '../../common/ExpandableText';
import { caseStateTone, clearsReviewDraft, formatCaseState, getReviewAccess } from '../caseState';
import CourseBadge from '../CourseBadge';
import ReviewOwnership from '../ReviewOwnership';
import StatusHistory from '../StatusHistory';
import DecisionPanel from './DecisionPanel';
import SessionDetailPanel from './SessionDetailPanel';
import SessionNoteDock from './SessionNoteDock';
import SessionRail from './SessionRail';
import StatusOverrideDialog from './StatusOverrideDialog';
import { formatSessionDate, sessionSortValue } from './sessionDisplay';
import { compileReviewNote, MAX_NOTE_LENGTH, useReviewDraft } from './useReviewDraft';
import ConfirmDialog from '../../ConfirmDialog';

const Transition = forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction='up' ref={ref} {...props} />;
});

/** Arrow keys move between sessions, but never while the reviewer is writing. */
const isTypingTarget = (target: EventTarget | null) => {
    const element = target as HTMLElement | null;
    if (!element) return false;
    return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.isContentEditable;
};

interface ReviewWorkspaceContentProps {
    initialCase: CaseType;
    onClose: () => void;
}

type CaseAction = 'claim' | 'release' | 'takeover';

const ReviewWorkspaceContent = ({ initialCase, onClose }: ReviewWorkspaceContentProps) => {
    const { user } = useContext(UserContext);
    const { reviewCases, recordReviewStatus } = useContext(ReviewCasesContext);
    const { reviewSessions, getSessionDetail, sessionDetails, loadingReviewSessions, reviewSessionsError, refreshReviewSessions } = useContext(ReviewSessionsContext);
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [viewedSessionIds, setViewedSessionIds] = useState<number[]>([]);
    const [noteSheetOpen, setNoteSheetOpen] = useState(false);
    const [decisionOpen, setDecisionOpen] = useState(false);
    const [discardOpen, setDiscardOpen] = useState(false);
    const [takeoverOpen, setTakeoverOpen] = useState(false);
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [pendingAction, setPendingAction] = useState<CaseAction | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [recordedStatuses, setRecordedStatuses] = useState<CaseStatusType[]>([]);
    const detailScroll = useRef<HTMLDivElement>(null);
    const actionInFlight = useRef(false);

    // The case may drop out of the filtered queue after a change, so keep what this view recorded.
    const reviewCase = useMemo(() => {
        const listed = reviewCases.find(item => item.id === initialCase.id) ?? initialCase;
        return { ...listed, statuses: _.uniqBy([...(listed.statuses ?? []), ...recordedStatuses], 'id') };
    }, [reviewCases, initialCase, recordedStatuses]);
    const access = getReviewAccess(reviewCase, user.id);
    const modalOpen = decisionOpen || noteSheetOpen || discardOpen || takeoverOpen || overrideOpen || historyOpen;

    const { draft, saveStatus, saveError, setSessionNote, discardDraft, notedSessionIds, updateDraft, flushDraft } = useReviewDraft(
        user.id,
        reviewCase.id,
    );

    const sessions = useMemo(
        () =>
            reviewSessions
                .filter((session: SessionType) => session.case === reviewCase.id)
                .sort((a, b) => sessionSortValue(a.date) - sessionSortValue(b.date)),
        [reviewSessions, reviewCase.id],
    );

    const currentSession: SessionType | undefined = sessions[selectedIndex];
    const compiledNote = useMemo(() => compileReviewNote(draft, sessions), [draft, sessions]);
    const latestState = access.state;
    const ready = !loadingReviewSessions && !reviewSessionsError && (!currentSession || !!sessionDetails[currentSession.id]);
    const close = useCallback(() => { if (flushDraft()) onClose(); }, [flushDraft, onClose]);

    const runAction = async (action: CaseAction) => {
        // State updates only after a re-render, so a quick second click would slip past it.
        if (actionInFlight.current) return;
        actionInFlight.current = true;
        setPendingAction(action);
        setActionError(null);
        const result = await recordReviewStatus(reviewCase.id, {
            state: action === 'release' ? 'SUBMITTED' : 'IN_REVIEW',
            takeover: action === 'takeover',
            expectedStatusId: access.latestStatusId,
        });
        setPendingAction(null);
        actionInFlight.current = false;
        setTakeoverOpen(false);
        if (!result.ok) {
            setActionError(result.error);
            return;
        }
        const saved = result.status;
        if (saved) setRecordedStatuses(previous => [...previous, saved]);
    };

    useEffect(() => { refreshReviewSessions(); }, [refreshReviewSessions]);

    useEffect(() => {
        if (detailScroll.current) detailScroll.current.scrollTop = 0;
    }, [currentSession?.id]);

    const markViewed = useCallback((sessionId: number) => {
        setViewedSessionIds(previous => previous.includes(sessionId) ? previous : [...previous, sessionId]);
    }, []);

    const goTo = useCallback(
        (index: number) => setSelectedIndex(Math.min(Math.max(index, 0), Math.max(sessions.length - 1, 0))),
        [sessions.length],
    );

    useEffect(() => {
        if (selectedIndex > sessions.length - 1) {
            setSelectedIndex(Math.max(sessions.length - 1, 0));
        }
    }, [sessions.length, selectedIndex]);

    useEffect(() => {
        if (loadingReviewSessions || reviewSessionsError) return;
        const next = sessions[selectedIndex + 1];
        if (next) {
            getSessionDetail(next.id).catch(() => undefined);
        }
    }, [sessions, selectedIndex, getSessionDetail, loadingReviewSessions, reviewSessionsError]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (modalOpen || isTypingTarget(event.target) || event.altKey || event.ctrlKey || event.metaKey) return;
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                goTo(selectedIndex + 1);
            } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                goTo(selectedIndex - 1);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [modalOpen, goTo, selectedIndex]);

    const handleSubmitted = () => {
        setSubmitted(true);
        setDecisionOpen(false);
        if (discardDraft()) onClose();
    };

    const currentNote = currentSession ? draft.sessionNotes[currentSession.id] ?? '' : '';
    const sessionLabel = currentSession
        ? `Session ${selectedIndex + 1} · ${formatSessionDate(currentSession.date)}`
        : '';
    const viewedCount = sessions.filter(session => viewedSessionIds.includes(session.id)).length;
    const progress = sessions.length === 0 ? 0 : (viewedCount / sessions.length) * 100;
    const decisionLabel = access.isMine ? (isMdUp ? 'Finish review' : 'Finish')
        : latestState === 'ESCALATED' ? 'Respond' : 'Record decision';
    const viewingHint = access.canClaim ? 'You are viewing this case. Start review to let other reviewers know you are working on it.'
        : !access.isMine && !access.claimedByOther && access.decisions.length === 0 ? 'No reviewer action is available for this case.' : null;

    const noteDock = currentSession && (
        <SessionNoteDock
            sessionLabel={sessionLabel}
            value={currentNote}
            onChange={(note) => setSessionNote(currentSession.id, note)}
            saveStatus={saveStatus}
            overallNote={draft.overallNote}
            onOverallChange={overallNote => updateDraft({ overallNote })}
            variant={isLgUp ? 'inline' : 'sheet'}
        />
    );

    return (
        <Dialog fullScreen open onClose={() => { if (!modalOpen) close(); }} TransitionComponent={Transition} aria-label='Case review workspace'>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <AppBar position='static' color='inherit' elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Toolbar sx={{ gap: 1, py: 1, flexWrap: 'wrap' }}>
                    <IconButton edge='start' onClick={close} aria-label='Close review'>
                        <CloseRoundedIcon />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                            <CourseBadge course={reviewCase.cohort?.course} />
                            <Typography variant='subtitle1' sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                {`Case ${reviewCase.id} · ${reviewCase.name || 'Untitled'}`}
                            </Typography>
                            <Chip
                                size='small'
                                label={formatCaseState(latestState)}
                                color={caseStateTone(latestState)}
                                variant='outlined'
                            />
                        </Stack>
                        <Typography variant='body2' component='div' sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                            <Box component='span' sx={{ fontWeight: 700 }}>Case type: </Box>
                            {reviewCase.type?.trim() || 'Not specified'}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' component='div' sx={{ overflowWrap: 'anywhere' }}>
                            {`${reviewCase.cohort?.course?.name ?? ''} (${reviewCase.cohort?.name ?? ''}) · ${viewedCount} of ${sessions.length} viewed · ${notedSessionIds.length} notes`}
                        </Typography>
                        <ReviewOwnership access={access} />
                        {viewingHint && <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>{viewingHint}</Typography>}
                    </Box>
                    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                        {access.canClaim && (
                            <LoadingButton variant='contained' loading={pendingAction === 'claim'} disabled={submitted} onClick={() => runAction('claim')}>
                                Start review
                            </LoadingButton>
                        )}
                        {access.claimedByOther && (
                            <Button variant='outlined' disabled={submitted || !!pendingAction} onClick={() => setTakeoverOpen(true)}>
                                Take over
                            </Button>
                        )}
                        {access.isMine && (
                            <LoadingButton color='inherit' loading={pendingAction === 'release'} disabled={submitted} onClick={() => runAction('release')}>
                                Release
                            </LoadingButton>
                        )}
                        {access.decisions.length > 0 && (
                            <Button disabled={!ready || submitted || !!pendingAction} variant='contained' onClick={() => setDecisionOpen(true)}>
                                {decisionLabel}
                            </Button>
                        )}
                        <Tooltip title='More actions'>
                            <IconButton
                                aria-label='More actions'
                                aria-haspopup='menu'
                                aria-expanded={!!menuAnchor}
                                disabled={submitted || !!pendingAction}
                                onClick={event => setMenuAnchor(event.currentTarget)}
                            >
                                <MoreVertRoundedIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                            <MenuItem onClick={() => { setMenuAnchor(null); setOverrideOpen(true); }}>Change status…</MenuItem>
                        </Menu>
                    </Stack>
                </Toolbar>
                <LinearProgress variant='determinate' value={progress} sx={{ height: 3 }} />
            </AppBar>
            <Stack direction='row' alignItems='center' flexWrap='wrap' sx={{ px: 2, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography role='status' variant='caption' sx={{ flex: 1 }}>{saveStatus}</Typography>
                <Button size='small' color='inherit' startIcon={<HistoryRoundedIcon />} onClick={() => setHistoryOpen(true)} aria-haspopup='dialog'>
                    {`History (${reviewCase.statuses.length})`}
                </Button>
                <Tooltip title='Discard draft'><IconButton aria-label='Discard draft' onClick={() => setDiscardOpen(true)}><DeleteOutlineRoundedIcon /></IconButton></Tooltip>
                <Tooltip title='Refresh sessions'><span><IconButton aria-label='Refresh sessions' disabled={loadingReviewSessions || submitted} onClick={refreshReviewSessions}><RefreshRoundedIcon /></IconButton></span></Tooltip>
            </Stack>
            {saveError && <Alert severity='error' action={<Button color='inherit' onClick={flushDraft}>Retry save</Button>}>{saveError}</Alert>}
            {actionError && <Alert severity='error' onClose={() => setActionError(null)}>{actionError}</Alert>}
            {submitted && <Alert severity='success'>Review submitted. The browser draft could not be removed; do not submit again.</Alert>}

            <Box sx={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: isMdUp ? 'row' : 'column' }}>
                <SessionRail
                    sessions={sessions}
                    selectedIndex={selectedIndex}
                    onSelect={goTo}
                    viewedSessionIds={viewedSessionIds}
                    notedSessionIds={notedSessionIds}
                    orientation={isMdUp ? 'vertical' : 'horizontal'}
                />

                <Box ref={detailScroll} sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: { xs: 2, md: 3 }, bgcolor: 'background.paper' }}>
                    <Box component='section' aria-labelledby='case-purpose-heading' sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                        <Typography id='case-purpose-heading' variant='overline' component='h2' sx={{ fontWeight: 700, color: 'text.secondary', lineHeight: 1.8 }}>
                            Case purpose
                        </Typography>
                        <ExpandableText text={reviewCase.purpose} emptyText='No purpose provided.' />
                    </Box>
                    {loadingReviewSessions ? <Box role='status'><LinearProgress /><Typography sx={{ mt: 2 }}>Loading sessions...</Typography></Box> : reviewSessionsError ? (
                        <Alert severity='error' action={<Button color='inherit' onClick={refreshReviewSessions}>Retry</Button>}>{reviewSessionsError}</Alert>
                    ) : currentSession ? (
                        <Stack spacing={2}>
                            <SessionDetailPanel
                                onLoaded={markViewed}
                                session={currentSession}
                                index={selectedIndex}
                                total={sessions.length}
                            />
                            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                                <Button
                                    startIcon={<ChevronLeftRoundedIcon />}
                                    onClick={() => goTo(selectedIndex - 1)}
                                    disabled={selectedIndex === 0}
                                >
                                    Previous
                                </Button>
                                <Typography variant='caption' color='text.secondary'>
                                    {`${selectedIndex + 1} / ${sessions.length}`}
                                </Typography>
                                <Button
                                    endIcon={<ChevronRightRoundedIcon />}
                                    onClick={() => goTo(selectedIndex + 1)}
                                    disabled={selectedIndex >= sessions.length - 1}
                                >
                                    Next
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography color='text.secondary'>No sessions were recorded for this case.</Typography>
                            <Typography variant='body2' color='text.disabled'>
                                You can still record an overall note.
                            </Typography>
                            <TextField label='Overall note' multiline fullWidth minRows={5} value={draft.overallNote} onChange={event => updateDraft({ overallNote: event.target.value })} inputProps={{ maxLength: MAX_NOTE_LENGTH }} sx={{ mt: 3 }} />
                        </Box>
                    )}
                </Box>

                {isLgUp && noteDock}
            </Box>

            {!isLgUp && currentSession && (
                <>
                    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 1 }}>
                        <Button
                            fullWidth
                            variant='outlined'
                            startIcon={
                                <Badge color='primary' variant='dot' invisible={!currentNote.trim()}>
                                    <EditNoteRoundedIcon />
                                </Badge>
                            }
                            onClick={() => setNoteSheetOpen(true)}
                        >
                            {currentNote.trim() ? 'Edit your note' : 'Add a note'}
                        </Button>
                    </Box>
                    <Drawer
                        anchor='bottom'
                        open={noteSheetOpen}
                        onClose={() => setNoteSheetOpen(false)}
                        sx={{ zIndex: theme.zIndex.modal + 1 }}
                        PaperProps={{ sx: { bgcolor: 'background.paper', maxHeight: '80dvh', overflowY: 'auto', borderTopLeftRadius: 8, borderTopRightRadius: 8, pb: 'env(safe-area-inset-bottom)' } }}
                    >
                        {noteDock}
                        <Box sx={{ px: 2, pb: 2 }}>
                            <Button fullWidth variant='contained' onClick={() => setNoteSheetOpen(false)}>
                                Done
                            </Button>
                        </Box>
                    </Drawer>
                </>
            )}

            <DecisionPanel
                draft={draft}
                updateDraft={updateDraft}
                flushDraft={flushDraft}
                ready={ready && !submitted}
                open={decisionOpen}
                onClose={() => setDecisionOpen(false)}
                caseId={reviewCase.id}
                compiledNote={compiledNote}
                noteCount={notedSessionIds.length}
                onSubmitted={handleSubmitted}
                decisions={access.decisions}
                expectedStatusId={access.latestStatusId}
            />
            <ConfirmDialog
                open={takeoverOpen}
                title='Take over this case?'
                message={`${access.holderName ?? 'Another reviewer'} is currently reviewing this case. Taking over moves it to you and is recorded in the case history.`}
                confirmText='Take over'
                cancelText='Cancel'
                onCancel={() => setTakeoverOpen(false)}
                onConfirm={() => runAction('takeover')}
            />
            <StatusOverrideDialog
                open={overrideOpen}
                onClose={() => setOverrideOpen(false)}
                caseId={reviewCase.id}
                access={access}
                hasDraft={notedSessionIds.length > 0 || !!draft.overallNote.trim() || !!draft.finalNote?.trim()}
                onChanged={(state, saved) => {
                    setOverrideOpen(false);
                    if (saved) setRecordedStatuses(previous => [...previous, saved]);
                    if (clearsReviewDraft(state)) discardDraft();
                }}
            />
            <Drawer
                anchor={isMdUp ? 'right' : 'bottom'}
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                sx={{ zIndex: theme.zIndex.modal + 1 }}
                PaperProps={{
                    role: 'dialog',
                    'aria-labelledby': 'case-history-title',
                    sx: {
                        bgcolor: 'background.paper', width: isMdUp ? 420 : '100%', maxHeight: isMdUp ? '100%' : '85dvh',
                        borderTopLeftRadius: isMdUp ? 0 : 8, borderTopRightRadius: isMdUp ? 0 : 8, pb: 'env(safe-area-inset-bottom)',
                    },
                }}
            >
                <Stack direction='row' alignItems='center' sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography id='case-history-title' variant='subtitle1' sx={{ fontWeight: 700, flex: 1 }}>Status history</Typography>
                    <IconButton aria-label='Close history' onClick={() => setHistoryOpen(false)}><CloseRoundedIcon /></IconButton>
                </Stack>
                <Box sx={{ p: 2, overflowY: 'auto' }}>
                    <StatusHistory statuses={reviewCase.statuses} />
                </Box>
            </Drawer>
            <ConfirmDialog open={discardOpen} title='Discard this review draft?' message='All unsubmitted notes and final-message edits for this case will be removed from this browser.' onCancel={() => setDiscardOpen(false)} onConfirm={() => { if (discardDraft()) { setDiscardOpen(false); onClose(); } }} />
        </Box>
        </Dialog>
    );
};

const ReviewWorkspace = () => {
    const { reviewWorkspaceCase, setReviewWorkspaceCase } = useContext(DialogsContext);
    const { user } = useContext(UserContext);
    const { currentUser } = useContext(AuthContext);
    const close = useCallback(() => setReviewWorkspaceCase(null), [setReviewWorkspaceCase]);

    return (
        <>
            {reviewWorkspaceCase && currentUser && user.is_case_reviewer && (
                <ReviewWorkspaceContent
                    key={`${currentUser.uid}:${user.id}:${reviewWorkspaceCase.id}`}
                    initialCase={reviewWorkspaceCase}
                    onClose={close}
                />
            )}
        </>
    );
};

export default ReviewWorkspace;
